/**
 * Canonical RBAC gate. The whole app should route role checks through
 * `can(user, ability)` instead of re-deriving them from `user.role`.
 *
 * Adding a new ability:
 *   1. Extend the `Ability` union below.
 *   2. Add an entry to `ABILITY_ROLES` listing which display-string roles
 *      from `@/features/auth/roles` are permitted.
 *   3. Special cases (org-scoped abilities like `switch:client`) live in
 *      the `can()` body — role alone is not enough.
 *
 * Roles are keyed by the same display strings (`MISSION FACILITATOR`,
 * `DATA ANALYST`, …) the rest of the SPA stores on `UserProfile.role`,
 * so the mapping table reads as a permissions matrix rather than a
 * claim-decoder. A null user (signed out / pre-hydration) has nothing.
 */
import {
  ROLE_ANALYST,
  ROLE_DIRECTOR,
  ROLE_ENGINEER,
  ROLE_FACILITATOR,
  ROLE_OBSERVER,
} from "@/features/auth/roles";
import type { UserProfile } from "@/content/users";
import { env, isZitadelMode } from "@/env";

export type Ability =
  | "manage:users"
  | "manage:program"
  | "upload:files"
  | "delete:files"
  | "manage:captures"
  | "view:questionnaire-results"
  | "switch:client"
  | "manage:sharing";

const ABILITY_ROLES: Record<Ability, ReadonlyArray<string>> = {
  "manage:users": [ROLE_FACILITATOR],
  // P0.8 — program-flow control: who is *permitted* to manage the program
  // (mark workshops complete, drive the unlock-next cascade). The
  // Facilitator runs the live session; the Director owns the program arc.
  // This is the role-level contract for the server boundary (DEP-SEC
  // role-isolation). The *UI* surfaces those controls behind power-user
  // (admin) mode (usePowerUser) — the same gate as Manage Crew — so a
  // role alone doesn't expose program-mutating controls in the client.
  "manage:program": [ROLE_FACILITATOR, ROLE_DIRECTOR],
  // Document-storage UI gating (interim feature). These mirror the BFF's
  // policy matrix (bff/app/policy.py) for the WRITE side so the UI doesn't
  // offer actions the BFF will 403 — but the BFF, validating the JWT and
  // checking the same matrix per tier, remains the real boundary. Roles
  // that can write/delete in *any* tier: Facilitator + Director + Analyst
  // (write), Facilitator only (delete). Per-tier nuance (e.g. only the
  // Facilitator may write `restricted`) is enforced server-side; the UI
  // gate is the coarse "is this user ever allowed to upload/delete?".
  "upload:files": [ROLE_FACILITATOR, ROLE_DIRECTOR, ROLE_ANALYST],
  "delete:files": [ROLE_FACILITATOR],
  // Captures grid moderation: delete any card (including another user's
  // saves and the authored seed cards) and reset a workshop's grid to
  // its authored state. Session hygiene is the facilitator's job.
  "manage:captures": [ROLE_FACILITATOR],
  // Questionnaire results (Asset Locker). Mirrors the BFF's read grants on
  // the `questionnaires` tier: participants can submit but only the
  // facilitation side may browse/aggregate responses.
  "view:questionnaire-results": [ROLE_FACILITATOR, ROLE_DIRECTOR, ROLE_ANALYST],
  // Multi-client tenancy. Role list is unused for the decision — see
  // `can()` / `isDxcHomeUser()`. Kept non-empty so unknown abilities stay
  // deny-by-default if someone calls ABILITY_ROLES directly.
  "switch:client": [
    ROLE_FACILITATOR,
    ROLE_DIRECTOR,
    ROLE_ANALYST,
    ROLE_ENGINEER,
    ROLE_OBSERVER,
  ],
  "manage:sharing": [ROLE_FACILITATOR],
};

/** True when the user is a DXC (facilitation-org) resident, not a client-org user. */
export function isDxcHomeUser(user: UserProfile): boolean {
  // Seed Manage Crew stamps affiliation explicitly ("dxc" | client id).
  if (user.affiliation) return user.affiliation === "dxc";
  if (user.orgId) {
    const dxcOrg = env.VITE_DXC_ORG_ID;
    return Boolean(dxcOrg && user.orgId === dxcOrg);
  }
  // Legacy seed rows (no affiliation) are DXC home. Zitadel users without
  // an org claim cannot be classified — deny rather than open the picker.
  return !isZitadelMode();
}

export function can(user: UserProfile | null, ability: Ability): boolean {
  if (!user) return false;
  if (ability === "switch:client") {
    // Mirrors BFF clients.py: DXC resident org alone — any role. Client-org
    // users never see the registry or send X-Client-Id.
    return isDxcHomeUser(user);
  }
  return ABILITY_ROLES[ability].includes(user.role);
}
